import { useState, useEffect } from 'react';
import { Eye, EyeOff, Check } from 'lucide-react';
import { AuthContainer } from './AuthContainer';
import { useAuth } from './AuthContext';
import { apiPost } from '../services/api.service';
import { useTranslation } from '../i18n/TranslationContext';

interface PasswordRequirement {
  text: string;
  met: boolean;
}

export function PasswordCreation() {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requirements, setRequirements] = useState<PasswordRequirement[]>([
    { text: t('auth.passwordCreation.requirements.minLength'), met: false },
    { text: t('auth.passwordCreation.requirements.number'), met: false },
    { text: t('auth.passwordCreation.requirements.uppercase'), met: false },
    { text: t('auth.passwordCreation.requirements.special'), met: false },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const { setCurrentStep, updateAuthData, authData } = useAuth();

  // Check for token on mount
  useEffect(() => {
    const emailVerificationToken = sessionStorage.getItem(
      'emailVerificationToken'
    );
    if (!emailVerificationToken) {
      setTokenExpired(true);
    }
    setIsCheckingToken(false);
  }, []);

  useEffect(() => {
    const newRequirements = [
      { text: t('auth.passwordCreation.requirements.minLength'), met: password.length >= 8 },
      { text: t('auth.passwordCreation.requirements.number'), met: /\d/.test(password) },
      {
        text: t('auth.passwordCreation.requirements.uppercase'),
        met: /[A-Z]/.test(password),
      },
      {
        text: t('auth.passwordCreation.requirements.special'),
        met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      },
    ];
    setRequirements(newRequirements);
  }, [password]);

  const allRequirementsMet = requirements.every(req => req.met);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRequirementsMet) return;

    setIsLoading(true);
    setError('');

    try {
      // Get the email verification token from sessionStorage
      const emailVerificationToken = sessionStorage.getItem(
        'emailVerificationToken'
      );

      if (!emailVerificationToken) {
        setTokenExpired(true);
        return;
      }

      const response = await apiPost(
        '/auth/create-password',
        {
          extraId: authData.extraId,
          password,
          emailVerificationToken,
        },
        {
          notification: {
            showSuccess: true, // Keep success notification
            showError: true, // Keep error notification
          },
        }
      );

      if (response.success) {
        // Store tokens for auto-login
        if (response.data.accessToken) {
          localStorage.setItem('accessToken', response.data.accessToken);
        }
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }

        // Update auth data with password (for context)
        updateAuthData({ password });

        // Move to next step
        setCurrentStep(5); // Go to profile information
      }
    } catch (err: any) {
      console.error('Password creation error:', err);

      // Check if it's a token expiration error
      const errorMessage = err?.message || err?.data?.message || '';
      if (
        errorMessage &&
        (errorMessage.toLowerCase().includes('expired') ||
          errorMessage.toLowerCase().includes('email verification') ||
          errorMessage.toLowerCase().includes('invalid or expired'))
      ) {
        setTokenExpired(true);
        sessionStorage.removeItem('emailVerificationToken');
      } else {
        setError(errorMessage || t('auth.passwordCreation.failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(3);
  };

  const handleVerifyEmail = () => {
    // Clear the expired token from session
    sessionStorage.removeItem('emailVerificationToken');
    // Set a flag to auto-send email on navigation
    sessionStorage.setItem('autoSendEmail', 'true');
    // Navigate to email verification step
    setCurrentStep(1);
  };

  // Show nothing while checking token to prevent flash
  if (isCheckingToken) {
    return null;
  }

  // If token is expired, show special UI
  if (tokenExpired) {
    return (
      <AuthContainer showBack={false} showProgress>
        <div className="text-center">
          <h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-4 leading-tight">
            {t('auth.passwordCreation.emailVerificationExpired')}
          </h1>
          <p className="text-[var(--ucm-text-medium)] mb-8">
            {t('auth.passwordCreation.pleaseVerifyAgain')}
          </p>
          <button
            onClick={handleVerifyEmail}
            className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] disabled:bg-[var(--ucm-text-light)] disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2"
          >
            {t('auth.passwordCreation.verifyEmail')}
          </button>
        </div>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer showBack={false} showProgress>
      <div className="text-center mb-8">
        <h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-8 leading-tight">
          {t('auth.passwordCreation.title')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
            {t('auth.passwordCreation.passwordLabel')}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full h-14 px-4 pr-12 border border-[var(--ucm-border-light)] rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
              placeholder={t('auth.passwordCreation.passwordPlaceholder')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] hover:text-[var(--ucm-text-medium)]"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {requirements.map((requirement, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div
                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                  requirement.met
                    ? 'bg-[var(--ucm-success-green)]'
                    : 'bg-[var(--ucm-border-light)]'
                }`}
              >
                {requirement.met && <Check className="w-3 h-3 text-white" />}
              </div>
              <span
                className={`text-sm ${
                  requirement.met
                    ? 'text-[var(--ucm-success-green)]'
                    : 'text-[var(--ucm-text-light)]'
                }`}
              >
                {requirement.text}
              </span>
            </div>
          ))}
        </div>

        {error && (
          <div className="text-red-500 text-sm text-center">{error}</div>
        )}

        <button
          type="submit"
          disabled={!allRequirementsMet || isLoading}
          className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] disabled:bg-[var(--ucm-text-light)] disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2"
        >
          {isLoading ? t('auth.passwordCreation.creating') : t('common.continue')}
        </button>
      </form>
    </AuthContainer>
  );
}
